import React from 'react';
import { riskScoreColour, RiskScoreColour, RiskScoreDirection } from './riskScoreColour';

export type { RiskScoreDirection };

export type RiskScoreCellProps = {
    value: number | null;
    direction: RiskScoreDirection;
    layout?: 'stacked' | 'inline' | 'bar-only';
};

const FILL_CLASS: Record<RiskScoreColour, string> = {
    'divergent-red-amber-green-1': 'bg-divergent-red-amber-green-1',
    'divergent-red-amber-green-2': 'bg-divergent-red-amber-green-2',
    'divergent-red-amber-green-3': 'bg-divergent-red-amber-green-3',
    'divergent-red-amber-green-4': 'bg-divergent-red-amber-green-4',
    'divergent-red-amber-green-5': 'bg-divergent-red-amber-green-5',
};

const RiskScoreCell = ({ value, direction, layout = 'stacked' }: RiskScoreCellProps) => {
    if (layout === 'bar-only') {
        if (value === null) {
            return <div data-testid='risk-score-bar-empty' className='h-1.5 w-full rounded-full bg-grey-20 overflow-hidden' />;
        }
        const colour = riskScoreColour(value, direction);
        const fillPercent = Math.max(0, Math.min(1, value)) * 100;
        return (
            <div className='h-1.5 w-full rounded-full bg-grey-20 overflow-hidden'>
                <div
                    data-testid='risk-score-bar-fill'
                    data-colour={colour}
                    className={`h-full rounded-full ${FILL_CLASS[colour]}`}
                    style={{ width: `${fillPercent}%` }}
                />
            </div>
        );
    }

    if (layout === 'inline') {
        if (value === null) {
            return (
                <div className='flex items-center gap-2'>
                    <span className='text-sm text-grey-60 tabular-nums'>—</span>
                    <div data-testid='risk-score-bar-empty' className='h-1.5 w-14 rounded-full bg-grey-20 overflow-hidden shrink-0' />
                </div>
            );
        }
        const colour = riskScoreColour(value, direction);
        const fillPercent = Math.max(0, Math.min(1, value)) * 100;
        return (
            <div className='flex items-center gap-2'>
                <span className='text-sm text-grey-100 tabular-nums'>{value.toFixed(2)}</span>
                <div className='h-1.5 w-14 rounded-full bg-grey-20 overflow-hidden shrink-0'>
                    <div
                        data-testid='risk-score-bar-fill'
                        data-colour={colour}
                        className={`h-full rounded-full ${FILL_CLASS[colour]}`}
                        style={{ width: `${fillPercent}%` }}
                    />
                </div>
            </div>
        );
    }

    if (value === null) {
        return (
            <div className='flex flex-col gap-1 w-full'>
                <span className='text-sm text-grey-60 tabular-nums'>—</span>
                <div data-testid='risk-score-bar-empty' className='h-1.5 w-full rounded-full bg-grey-20 overflow-hidden' />
            </div>
        );
    }
    const colour = riskScoreColour(value, direction);
    const fillPercent = Math.max(0, Math.min(1, value)) * 100;
    return (
        <div className='flex flex-col gap-1 w-full'>
            <span className='text-sm text-grey-100 tabular-nums'>{value.toFixed(2)}</span>
            <div className='h-1.5 w-full rounded-full bg-grey-20 overflow-hidden'>
                <div
                    data-testid='risk-score-bar-fill'
                    data-colour={colour}
                    className={`h-full rounded-full ${FILL_CLASS[colour]}`}
                    style={{ width: `${fillPercent}%` }}
                />
            </div>
        </div>
    );
};

export default RiskScoreCell;
